// Canonical character-draft shape + transitions, shared by every UI that
// edits or hydrates a character before sending it to the backend.
//
// Why this exists: the campaign-setup wizard and the /characters editor used
// to maintain divergent local state shapes for the same underlying character.
// Each flow hand-copied a subset of fields from API responses into its own
// state, which let BOU-926 slip through three times — a field (stage_cutout_url)
// existed on the backend, was returned by /api/characters/mine, but never made
// it into the wizard slot because the wizard's hand-copy didn't list it. Both
// flows now consume this hook so the field list lives in exactly one place.
//
// State ownership: the hook owns draft state via useState. /characters runs it
// once for its single editor. The wizard parent will eventually run it per
// slot via a <CampaignSlotEditor> that exposes its draft via an imperative
// ref (follow-up). Until then, the wizard imports `cloneFromSource` and
// `mergeStageAssetUpdate` directly to populate its slot array — the field
// list is still authoritative.

import { useCallback, useState } from 'react';

// Canonical field list. Adding a new character-shaped field? Add it here
// once and every UI that uses the hook (or the helpers) picks it up.
export const CHARACTER_DRAFT_FIELDS = Object.freeze([
  // Identity
  'character_id',
  'external_character_id',
  'character_type',
  // Profile
  'name',
  'character_class',
  'race',
  'level',
  'concept',
  'description',
  'backstory',
  'visual_description',
  // Visual metadata
  'gender',
  'age_category',
  'build',
  'height_description',
  'facial_expression',
  'facial_features',
  'attire',
  'primary_weapon',
  'distinguishing_feature',
  'background_setting',
  'pose',
  // Portrait
  'portrait_url',
  'portrait_path',
  'portrait_prompt',
  'portrait_cdn_thumbnail_urls',
  // Voice
  'voice_id',
  'voice_archetype',
  'voice_settings',
  // Visual style + image model
  'visual_style',
  'image_style',
  // Stage assets (the BOU-926 fields)
  'full_body_portrait_url',
  'full_body_portrait_path',
  'full_body_portrait_prompt',
  'stage_full_body_url',
  'stage_full_body_path',
  'stage_full_body_prompt',
  'stage_cutout_url',
  'stage_cutout_path',
  'stage_asset_validation',
  'image_assets',
]);

// Subset that travels through Stage Cutout generation/regeneration. Both
// /characters and the wizard use this when merging the server's response
// back into their local draft after queueStageCutout resolves.
export const STAGE_ASSET_DRAFT_FIELDS = Object.freeze([
  'full_body_portrait_url',
  'full_body_portrait_path',
  'full_body_portrait_prompt',
  'stage_full_body_url',
  'stage_full_body_path',
  'stage_full_body_prompt',
  'stage_cutout_url',
  'stage_cutout_path',
  'stage_asset_validation',
  'image_assets',
]);

const OBJECT_FIELDS = new Set([
  'image_assets',
  'stage_asset_validation',
  'voice_settings',
  'portrait_cdn_thumbnail_urls',
]);

const defaultFor = (key) => {
  if (key === 'level') return 1;
  if (OBJECT_FIELDS.has(key)) return null;
  return '';
};

/** Build an empty draft with every canonical field present. */
export const emptyDraft = () => CHARACTER_DRAFT_FIELDS.reduce((d, k) => {
  d[k] = defaultFor(k);
  return d;
}, {});

/**
 * Clone a backend character (or any character-shaped object) into a fresh
 * draft. Fields missing on the source get their canonical default rather
 * than `undefined`, so consumers can safely read every field.
 */
export const cloneFromSource = (source = {}) => CHARACTER_DRAFT_FIELDS.reduce((d, k) => {
  d[k] = (source[k] !== undefined && source[k] !== null)
    ? source[k]
    : defaultFor(k);
  return d;
}, {});

/**
 * Merge a Stage Cutout generation response back into a draft. Only the
 * stage-asset fields present on the response are applied; other draft
 * fields (name, class, backstory, etc.) are left untouched.
 */
export const mergeStageAssetUpdate = (draft, updated = {}) => {
  if (!updated || typeof updated !== 'object') return draft;
  const patch = { ...draft };
  // character_id may be assigned for the first time when an eager-create
  // happens during Stage Cutout generation; allow it through.
  if (updated.character_id) patch.character_id = updated.character_id;
  for (const key of STAGE_ASSET_DRAFT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(updated, key)) {
      patch[key] = updated[key];
    }
  }
  return patch;
};

/**
 * useCharacterDraft — state-owning React hook for a single character draft.
 *
 * Both the /characters editor and (eventually) each campaign-setup slot
 * consume this. The hook is internally a `useState` over the draft object;
 * the value returned exposes setters that always preserve the canonical
 * shape. Consumers never reach in and spread the draft themselves — that
 * was the source of BOU-926 and similar field-drop bugs.
 */
export const useCharacterDraft = (initialSource = null) => {
  const [draft, setDraft] = useState(() => (
    initialSource ? cloneFromSource(initialSource) : emptyDraft()
  ));

  const setField = useCallback((field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
  }, []);

  const setFields = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const hydrateFromSource = useCallback((source) => {
    setDraft(cloneFromSource(source));
  }, []);

  const mergeStageAssets = useCallback((updated) => {
    setDraft((d) => mergeStageAssetUpdate(d, updated));
  }, []);

  const reset = useCallback(() => {
    setDraft(emptyDraft());
  }, []);

  return {
    draft,
    setDraft,
    setField,
    setFields,
    hydrateFromSource,
    mergeStageAssets,
    reset,
  };
};

export default useCharacterDraft;
